import type { ErrorEvent } from "@sentry/react";

/**
 * Map libraries whose errors we tag rather than drop, so they can be triaged or
 * muted by tag without going blind to real bugs surfacing through them.
 *
 * Matched against the throw-site frame's minified filename. The `*-vendor`
 * names come from `manualChunks` in vite.config.ts. Google's maps engine also
 * loads externally from maps.googleapis.com, since its terms forbid bundling it
 * the way mapbox-gl and maplibre-gl are.
 */
const MAP_VENDOR_PATTERNS: { vendor: string; pattern: RegExp }[] = [
  { vendor: "maplibre", pattern: /maplibre-vendor/ },
  { vendor: "mapbox", pattern: /mapbox-vendor/ },
  {
    vendor: "google-maps",
    pattern: /google-maps-vendor|maps\.(googleapis|gstatic)\.com/,
  },
];

/**
 * Returns which map library an exception was thrown from, or null.
 *
 * Sentry orders frames oldest-first, so the last frame is the throw site. For
 * chained exceptions the most recent value is the relevant one.
 */
export const getMapVendor = (event: ErrorEvent): string | null => {
  const values = event.exception?.values;
  if (!values?.length) return null;

  const frames = values[values.length - 1]?.stacktrace?.frames;
  if (!frames?.length) return null;

  const topFrame = frames[frames.length - 1];
  const file = topFrame?.filename || topFrame?.abs_path || "";

  return (
    MAP_VENDOR_PATTERNS.find(({ pattern }) => pattern.test(file))?.vendor ??
    null
  );
};
